import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SearchBox from "components/__new/common/SearchBox";
import Table from "components/__new/common/Table";
import { useLocation } from "react-router-dom";
import DownloadAsCSV from "components/__new/common/DownloadAsCSV";
import Print from "components/__new/common/Print";
import DynamicHeadTag from "components/__new/common/DynamicHeadTag";
import useUrlQueryParams from "hooks/useUrlQueryParams";
import { TableContent } from "components/__new/common/TableContent";
import { DateTimeFormat } from "utils/dateFormat";
import CurrencyFormat from "utils/currencyFormat";
import Pagination from "components/__new/common/pagination";
import Maybe from "components/__new/common/Maybe";
import processParams from "utils/processParams";
import * as historyAction from "store/entities/paymentHistory/action";
import TabSettings from "components/__new/common/TabSettings";


export const List = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const {urlQueryParams} = useUrlQueryParams();
    const { historyLoading, history, historyDownloadLoading, historyDownload } = useSelector((s) => s.entities.paymentHistory);
    
    
    const downloadAsCSVRef = useRef();
    const printComponentRef = useRef();
    const [listParams, setListParams] = useState({
        "page": 1,
        "limit": 20,
    });


    const addOtherFilters = (params) => {
        let paramsPayload = {...params};
        if (params.search){
            if (isNaN(params.search)){
                paramsPayload = {
                    ...paramsPayload,
                    invoiceId: params.search,
                }
                delete paramsPayload["amount"];
            }
            else{
                paramsPayload = {
                    ...paramsPayload,
                    amount: params.search,
                }
                delete paramsPayload["invoiceId"];
            }
            delete paramsPayload["search"];
        }

        return paramsPayload;
    }


    const getHistory = () => {
        let params = {...listParams};

        params = processParams(urlQueryParams, params, "page", "number", 1, "page");
        params = processParams(urlQueryParams, params, "limit", "number", 20, "limit");
        params = processParams(urlQueryParams, params, "search", "string", "", "search");
        setListParams(params);

        const paramsPayload = addOtherFilters(params);
        dispatch(historyAction.getAllHistory(paramsPayload));
    }


    useEffect(() => {
        getHistory();
        // eslint-disable-next-line
    }, [location]);


    return (
        <div>

            <DynamicHeadTag
                headerText="Payment history"
                headerDescription="View a history of all payment transactions made on your account."
            />

            <div className="border-b">
                <TabSettings options={[
                    {
                        title: <span>Payment Notes</span>,
                        type: "link",
                        url: "/payment-note",
                    },
                    {
                        title: <span>Payment History</span>,
                        type: "link",
                        url: "/payment-history",
                    },
                ]} />
            </div>

            <div className="mt-6 lg:flex">
                <div className="w-full lg:w-1/2">
                    <div className="page-title">
                        Payment History
                    </div>
                </div>
                <div className="w-full lg:w-1/2 flex mt-4 lg:mt-0 lg:justify-end space-x-3"></div>
            </div>

        
            <div className="mt-6 box !px-0 !pb-0">
                <div className="px-4 flex flex-wrap justify-between">
                    <div className="max-w-sm flex-grow sm:mr-6">
                        <SearchBox placeholder="Search by payment id, amount..." name="search" extraUrlParams="page=1" />
                    </div>
                    <div className="flex flex-wrap space-x-3 sm:space-x-6">
                        <div>
                            <DownloadAsCSV
                                ref={downloadAsCSVRef}
                                tooltip="Download payment history"
                                filename="Payment History.csv"
                                columns={[
                                    { label: "Date", key: "createdAt" },
                                    { label: "Payment ID", key: "invoiceId" },
                                    { label: "Amount", key: "amount" },
                                ]}
                                click={() => {
                                    dispatch(historyAction.downloadHistory(addOtherFilters({...listParams, page: 1, limit: 10000}))).then(() => {
                                        downloadAsCSVRef.current?.downloadData();
                                    });
                                }}
                                loading={historyDownloadLoading}
                                response={historyDownload?.paymentHistory?.docs}
                            />
                        </div>

                        <div>
                            <Print component={printComponentRef} />
                        </div>
                    </div>
                </div>
                
                <div className="mt-6">
                    <div ref={printComponentRef}>
                        
                        <div className="mb-4 text-xl hidden print:block">
                            Payment History
                        </div>

                        <Table>
                            <Table.Head>
                                <th>Date</th>
                                <th>Payment ID</th>
                                <th>Amount</th>
                            </Table.Head>
                            <Table.Body>

                                <TableContent
                                    loading={historyLoading}
                                    loadingText="Loading payment history"
                                    data={history?.paymentHistory}
                                    colspan={8}
                                />

                                {historyLoading === false && history?.paymentHistory?.docs?.map((historyData) =>
                                    <tr key={historyData._id}>
                                        <td>
                                            {DateTimeFormat(historyData.createdAt)}
                                        </td>
                                        <td>
                                            {historyData.invoiceId}
                                        </td>
                                        <td>
                                            {CurrencyFormat(historyData.amount)}
                                        </td>
                                    </tr>
                                )}

                            </Table.Body>
                        </Table>

                        <Maybe condition={historyLoading === false && history?.paymentHistory?.docs?.length > 0}>
                            <div className="mt-6">
                                <Pagination data={history?.paymentHistory} />
                            </div>
                        </Maybe>

                    </div>
                </div>
            </div>

        </div>
    )
}


export default List;